import { computePosition } from '@floating-ui/dom';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_LOW, SELECTION_CHANGE_COMMAND } from 'lexical';
import { forwardRef, JSX, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { getSelectedNode } from '~components/generic/editable-field/utils/getSelectedNode';
import { TrashIcon } from '~components/icons/TrashIcon';

type Coords = { x: number; y: number } | undefined;

const FloatingLinkViewer = forwardRef<
  HTMLDivElement,
  {
    url: string;
    onDelete: () => void;
    coords: Coords;
  }
>(({ url, onDelete, coords }, ref) => {
  const displayUrl = url.split('#')[0];

  console.log({ url });

  return (
    <div
      ref={ref}
      className="floating-link-viewer"
      style={{
        position: 'fixed',
        top: coords?.y + 90,
        left: coords?.x,
        visibility: coords ? 'visible' : 'hidden',
        opacity: coords ? 1 : 0,
      }}
    >
      <a href={url} target="_blank" rel="noopener noreferrer">
        {displayUrl}
      </a>

      <button onClick={onDelete}>
        <TrashIcon />
      </button>
    </div>
  );
});

FloatingLinkViewer.displayName = 'FloatingLinkViewer';

export default function FloatingLinkPlugin({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [coords, setCoords] = useState<Coords>(undefined);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setLinkUrl(null);
        setCoords(undefined);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calculatePosition = useCallback(() => {
    const domSelection = window.getSelection();
    const domRange = domSelection?.rangeCount !== 0 && domSelection?.getRangeAt(0);

    if (!domSelection || domSelection.isCollapsed) {
      setCoords(undefined);
      return;
    }

    if (!domRange || !ref.current) {
      setCoords(undefined);
      return;
    }

    // Adjust the position calculation to handle block elements
    computePosition(domRange, ref.current, { placement: 'top' })
      .then(pos => {
        // Ensure the position is calculated relative to the block element
        setCoords({ x: pos.x, y: pos.y - 10 });
      })
      .catch(() => {
        setCoords(undefined);
      });
  }, []);

  const updateLinkViewer = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Get the parent node instead of just the selected node
        const node = selection.getNodes()[0];
        const linkNode = $findMatchingParent(node, $isLinkNode);

        if ($isLinkNode(linkNode)) {
          setLinkUrl(linkNode.getURL());
          calculatePosition();
        } else {
          setLinkUrl(null);
          setCoords(undefined);
        }
      } else {
        setLinkUrl(null);
        setCoords(undefined);
      }
    });
  }, [editor, calculatePosition]);

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkViewer();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        TOGGLE_LINK_COMMAND,
        (payload: string | null) => {
          if (payload === null) {
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                const node = getSelectedNode(selection);
                const parent = $findMatchingParent(node, $isLinkNode);
                if ($isLinkNode(parent)) {
                  const children = parent.getChildren();
                  for (const child of children) {
                    parent.insertBefore(child);
                  }
                  parent.remove();
                }
              }
            });
          }
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, updateLinkViewer]);

  if (!linkUrl) {
    return null;
  }

  return createPortal(
    <FloatingLinkViewer
      ref={ref}
      url={linkUrl}
      coords={coords}
      onDelete={() => {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        setCoords(undefined);
      }}
    />,
    anchorElem,
  );
}
